import { LanguageCode, Translation } from './types';
import { Heart, Baby, Brain, Wind, Pill, Stethoscope } from 'lucide-react';

export const SUPPORTED_LANGUAGES = [
  { code: LanguageCode.EN, name: 'English',    nativeName: 'English'    },
  { code: LanguageCode.ZU, name: 'Zulu',       nativeName: 'isiZulu'    },
  { code: LanguageCode.XH, name: 'Xhosa',      nativeName: 'isiXhosa'   },
  { code: LanguageCode.AF, name: 'Afrikaans',  nativeName: 'Afrikaans'  },
  { code: LanguageCode.TN, name: 'Tswana',     nativeName: 'Setswana'   },
  { code: LanguageCode.TS, name: 'Tsonga',     nativeName: 'Xitsonga'   },
];

export const TRANSLATIONS: Record<LanguageCode, Translation> = {
  [LanguageCode.EN]: {
    welcomeTitle: 'SisterAI South Africa',
    welcomeSubtitle: 'Your AI nurse triage assistant. Get health guidance in your language — anytime, anywhere.',
    selectLanguage: 'Select your language to begin',
    inputPlaceholder: 'Describe your symptoms or health concern...',
    sendButton: 'Send',
    disclaimerTitle: 'Important Notice',
    disclaimerText: 'SisterAI is an AI health information tool, not a substitute for a doctor or nurse. Always seek professional medical care for serious concerns. In an emergency, call 112 (Emergency) or 10177 (Ambulance).',
    disclaimerFooter: 'SisterAI — Health information only. Always consult a healthcare professional.',
    loading: 'Consulting health database...',
    error: 'An error occurred. Please try again.',
    newChat: 'New Consultation',
    saveChat: 'Save Consultation',
    features: {
      title: 'What brings you in today?',
      items: [
        'I have chest pain or difficulty breathing',
        'My child has a high fever',
        'I have TB or flu-like symptoms',
        'I need mental health support',
        'I am pregnant and concerned',
        'I have a headache and dizziness',
      ],
    },
    triage: {
      emergency: '🔴 EMERGENCY',
      urgent: '🟠 URGENT',
      monitor: '🟡 MONITOR',
      selfcare: '🟢 SELF-CARE',
      emergencyAction: 'Call 112 or go to Emergency Room NOW',
      urgentAction: 'Go to a clinic or hospital TODAY',
      monitorAction: 'See a healthcare provider within 2–3 days',
      selfcareAction: 'Manage at home — follow guidance below',
    },
  },

  [LanguageCode.ZU]: {
    welcomeTitle: 'SisterAI Ningizimu Afrika',
    welcomeSubtitle: 'Umhlengikazi wakho we-AI. Thola iseluleko sezempilo ngolimi lwakho.',
    selectLanguage: 'Khetha ulimi lwakho ukuze uqale',
    inputPlaceholder: 'Chaza izimpawu zakho zokugula...',
    sendButton: 'Thumela',
    disclaimerTitle: 'Isaziso Esibalulekile',
    disclaimerText: 'I-SisterAI iyithuluzi lolwazi lwezempilo, hhayi ingubo yodokotela noma umhlengikazi. Hlala ufuna usizo lwezobuchwepheshe bezempilo ezinzimeni. Ezimweni eziphuthumayo, shaya 112 noma 10177.',
    disclaimerFooter: 'SisterAI — Ulwazi lwezempilo kuphela. Hlala ubona ongoti bezempilo.',
    loading: 'Ibuza ulwazi lwezempilo...',
    error: 'Kube nephutha. Sicela uzame futhi.',
    newChat: 'Ukuxhumana Okusha',
    saveChat: 'Gcina Ukuxhumana',
    features: {
      title: 'Yini ekuphatha namuhla?',
      items: [
        'Nginezinhlungu zeqhakaza noma ngilinde ukuphefumula',
        'Ingane yami inemvelo ephezulu',
        'Nginezimpawu ze-TB noma umkhuhlane',
        'Ngidinga usizo lwezempilo yengqondo',
        'Ngikhulelwe futhi ngikhathazekile',
        'Nginezinhlungu zekhanda nokuzungeza',
      ],
    },
    triage: {
      emergency: '🔴 ISIMO ESIPHUTHUMAYO',
      urgent: '🟠 KUPHUTHUMA',
      monitor: '🟡 QAPHELA',
      selfcare: '🟢 ZINAKEKELE EKHAYA',
      emergencyAction: 'Shaya 112 noma hamba e-Emergency Room MANJE',
      urgentAction: 'Hamba emtholampilo noma esibhedlela NAMUHLA',
      monitorAction: 'Bona umhlengikazi phakathi kwamalanga amabili noma amathathu',
      selfcareAction: 'Phatha ekhaya — landela iseluleko ngezansi',
    },
  },

  [LanguageCode.XH]: {
    welcomeTitle: 'SisterAI Mzantsi Afrika',
    welcomeSubtitle: 'Umongikazi wakho we-AI. Fumana iingcebiso zempilo ngolwimi lwakho.',
    selectLanguage: 'Khetha ulwimi lwakho ukuze uqalise',
    inputPlaceholder: 'Chaza iimpawu zakho zempilo...',
    sendButton: 'Thumela',
    disclaimerTitle: 'Isaziso Esibalulekileyo',
    disclaimerText: 'I-SisterAI sisixhobo solwazi lwempilo, hayi isithuba sokugqirha okanye umongikazi. Hlala ufuna unyango lwengcali kwimicimbi eshushu. Kwimeko zochotho, tsalela 112 okanye 10177.',
    disclaimerFooter: 'SisterAI — Ulwazi lwempilo kuphela. Yihlola nochwephesha wezempilo.',
    loading: 'Ibuza ulwazi lwezempilo...',
    error: 'Kubekho impazamo. Nceda uzame kwakhona.',
    newChat: 'Ingxoxo Entsha',
    saveChat: 'Gcina Ingxoxo',
    features: {
      title: 'Yintoni ekuphatha namhlanje?',
      items: [
        'Ndineentlungu zesifuba okanye ndingaphefumli',
        'Umntwana wam unomkhuhlane ophezulu',
        'Ndineempawu ze-TB okanye umkhuhlane',
        'Ndidinga uncedo lwezempilo yengqondo',
        'Ndikhulile kwaye ndikhathazekile',
        'Ndineentlungu zekhanda nokuzungeza',
      ],
    },
    triage: {
      emergency: '🔴 INGXAKI EKHAWULEZAYO',
      urgent: '🟠 IPHUTHUMAYO',
      monitor: '🟡 JONGA',
      selfcare: '🟢 ZIKHATHALELE EKHAYA',
      emergencyAction: 'Tsalela 112 okanye yiya kwiGumbi loXilongo NGOKU',
      urgentAction: 'Yiya kwisibhedlela okanye kliniki NAMHLANJE',
      monitorAction: 'Bona ugcisa kwisuku sama-2 ukuya ku-3',
      selfcareAction: 'Phatha ekhaya — landela iingcebiso ezingezantsi',
    },
  },

  [LanguageCode.AF]: {
    welcomeTitle: 'SisterAI Suid-Afrika',
    welcomeSubtitle: 'Jou AI-verpleegster. Kry gesondheidsleiding in jou taal.',
    selectLanguage: 'Kies jou taal om te begin',
    inputPlaceholder: 'Beskryf jou simptome of gesondheidsprobleem...',
    sendButton: 'Stuur',
    disclaimerTitle: 'Belangrike Kennisgewing',
    disclaimerText: 'SisterAI is \'n AI-gesondheidsinligtingstuiste, nie \'n plaasvervanger vir \'n dokter of verpleegster nie. Soek altyd professionele mediese sorg vir ernstige bekommernisse. In \'n noodgeval, skakel 112 of 10177.',
    disclaimerFooter: 'SisterAI — GesondheidsInfo slegs. Raadpleeg altyd \'n gesondheidswerker.',
    loading: 'Raadpleeg gesondheidsdatabasis...',
    error: '\'n Fout het voorgekom. Probeer asseblief weer.',
    newChat: 'Nuwe Konsultasie',
    saveChat: 'Stoor Konsultasie',
    features: {
      title: 'Wat pla jou vandag?',
      items: [
        'Ek het borspyn of asemhalingsmoeilikheid',
        'My kind het hoë koors',
        'Ek het TB- of griep-simptome',
        'Ek benodig geestesgesondheidsondersteuning',
        'Ek is swanger en bekommerd',
        'Ek het hoofpyn en duiseligheid',
      ],
    },
    triage: {
      emergency: '🔴 NOODGEVAL',
      urgent: '🟠 DRINGEND',
      monitor: '🟡 MONITEER',
      selfcare: '🟢 SELFVERSORGING',
      emergencyAction: 'Bel 112 of gaan NA die Noodkamer',
      urgentAction: 'Gaan VANDAG na \'n kliniek of hospitaal',
      monitorAction: 'Sien \'n gesondheidswerker binne 2–3 dae',
      selfcareAction: 'Behandel tuis — volg leiding hieronder',
    },
  },

  [LanguageCode.TN]: {
    welcomeTitle: 'SisterAI Aforika Borwa',
    welcomeSubtitle: 'Mooki wa gago wa AI. Bona kaelo ya boitekanelo ka puo ya gago.',
    selectLanguage: 'Kgetha puo ya gago go simolola',
    inputPlaceholder: 'Tlhalosa matshwao a gago a bolwetse...',
    sendButton: 'Romela',
    disclaimerTitle: 'Kitsiso ya Botlhokwa',
    disclaimerText: 'SisterAI ke sediriswa sa tshedimosetso ya boitekanelo, ga se e nne motlhatswa wa ngaka kgotsa mooki. Dira gore o batlisise tlhokomelo ya setlhopha sa balaodi ba tsa pholo go ya dikgwetlho tse di bokoa. Mo maemong a tshoganyetso, letsa 112 kgotsa 10177.',
    disclaimerFooter: 'SisterAI — Tshedimosetso ya boitekanelo fela. Boa ulaya ngaka ya gago.',
    loading: 'E a batla tshedimosetso ya boitekanelo...',
    error: 'Phoso e diragetse. Tswee-tswee leka gape.',
    newChat: 'Puisano e Ntsha',
    saveChat: 'Boloka Puisano',
    features: {
      title: 'Go go tshwenya eng gompieno?',
      items: [
        'Ke na le botlhoko jwa sehuba kgotsa mathata a go hema',
        'Ngwana wa me o na le mogote o mogolo',
        'Ke na le matshwao a TB kgotsa mokgweetsi',
        'Ke tlhoka thuso ya boitekanelo jwa kelello',
        'Ke imetse mme ke na le ditshwenyego',
        'Ke na le botlhoko jwa tlhogo le go phakela',
      ],
    },
    triage: {
      emergency: '🔴 TSHOGANYETSO',
      urgent: '🟠 GO TLHOKOFALA',
      monitor: '🟡 LEBELA',
      selfcare: '🟢 ITOKAFATSE GAE',
      emergencyAction: 'Letsa 112 kgotsa ya kwa Kamorong ya Tshoganyetso JAANONG',
      urgentAction: 'Ya kwa kliniki kgotsa kwa kokelong GOMPIENO',
      monitorAction: 'Bona motlhokomedi wa boitekanelo mo malatsing a 2-3',
      selfcareAction: 'Alafa gae — latela kaelo e e latelang',
    },
  },

  [LanguageCode.TS]: {
    welcomeTitle: 'SisterAI Afrika-Dzonga',
    welcomeSubtitle: 'Nawisi wa wena wa AI. Kuma xiyimo xa vuswi hi ririmi ra wena.',
    selectLanguage: 'Hlawula ririmi ra wena ku sungula',
    inputPlaceholder: 'Komba switimela swa wena swa vuvabyi...',
    sendButton: 'Rhuma',
    disclaimerTitle: 'Nhlayelo wa Nkoka',
    disclaimerText: 'SisterAI i xitirhisiwa xa vuxokoxoko xa vuswi, a si xitirhisiwa xa dokodela kumbe nawisi. Lava ntirho wa bochwephesha bya ndlela ya vuswi hikuva ka swikaritelo swa nkoka. Loko ku na timhaka ta ku xurha, ndzelela 112 kumbe 10177.',
    disclaimerFooter: 'SisterAI — Vuxokoxoko bya vuswi ntsena. Tivula mhaka ya nawisi.',
    loading: 'Ya lava vuxokoxoko bya vuswi...',
    error: 'Ku ve ni xihoxo. Hi kombela u ringeta nakambe.',
    newChat: 'Bulo Rintshwa',
    saveChat: 'Hlayisa Bulo',
    features: {
      title: 'Yini leyi ku pfunetaka namuntlha?',
      items: [
        'Ndina marihangu ya sifuva kumbe nkinga yo hema',
        'Nhwanyana wa mina una nghenha leyikulu',
        'Ndina switimela swa TB kumbe flhu',
        'Ndlela pfuno ya vuswi bya ntlawa',
        'Ndiri eka wanuna naswona ndina timhaka',
        'Ndina marihangu ya ntlhawa na ku phendereka',
      ],
    },
    triage: {
      emergency: '🔴 XU XURHA',
      urgent: '🟠 KU PFUMALEKA',
      monitor: '🟡 LANGUTA',
      selfcare: '🟢 XIKWEMBU EKA YINDLU',
      emergencyAction: 'Ndzelela 112 kumbe ya kwa Kamara ya Xurha SWESWI',
      urgentAction: 'Ya kwa kiliniki kumbe hospitala NAMUNTLHA',
      monitorAction: 'Vona mutirheli wa vuswi endhawini ya masiku ya 2-3',
      selfcareAction: 'Pheketa eka yindlu — landza xiyimo lexiya nga hansi',
    },
  },
};

export const FEATURE_ICONS = [Heart, Baby, Wind, Brain, Stethoscope, Pill];
